import styled, { css } from 'styled-components';
import { randomNumber, randomSign } from 'src/utils/numbers';
import { skeleton } from 'src/components/skeleton';

export const Video = styled.video`
  border-radius: ${({ theme }) => theme.bo.small};
  height: ${({ theme }) => theme.sz.s48};
  object-fit: fill;
  transition: transform 0.3s ease;
  width: ${({ theme }) => theme.sz.s48};
`;

export const Image = styled.img`
  border-radius: ${({ theme }) => theme.bo.small};
  height: ${({ theme }) => theme.sz.s48};
  transition: transform 0.3s ease;
  width: ${({ theme }) => theme.sz.s48};
`;

export const Placeholder = styled.div(
  ({ theme }) => css`
    background: ${theme.co.neutral99};
    border-radius: ${theme.bo.small};
    height: ${theme.sz.s40};
    object-fit: contain;
    width: auto;
    overflow: hidden;
    margin: ${theme.sz.s2};
  `,
);

export const TextContainer = styled.div(
  ({ theme }) => css`
    border-radius: ${theme.bo.small};
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: center;
    position: relative;
    transition: transform 0.3s ease;

    @media (min-width: 30rem) {
      justify-content: flex-start;
      margin-top: ${theme.sz.s3};
    }
  `,
);

export const TextBackground = styled.div(
  ({ theme }) => css`
    border-radius: ${theme.bo.small};
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: flex-start;
    padding: ${theme.sz.s4};
    position: relative;
    transition: background 0.3s ease;

    @media (min-width: ${theme.sz.md}) {
      border-radius: 0 0 ${theme.bo.small} ${theme.bo.small};
      padding: 0 ${theme.sz.s4} 0 ${theme.sz.s4};
    }
  `,
);

export const Title = styled.h2(
  ({ theme }) => css`
    color: ${theme.co.neutral20};
    font-family: ${theme.DEPRECATED_ty.boing};
    font-size: ${theme.DEPRECATED_ty.mediumLarge};
    text-align: left;
    margin: 0 0 0 ${theme.sz.s10};

    @media (min-width: ${theme.sz.sm}) {
      text-align: center;
      width: auto;
      margin: 0;
    }
  `,
);

export const Description = styled.p(
  ({ theme }) => css`
    color: ${theme.co.neutral20};
    font-size: ${theme.DEPRECATED_ty.smaller};
    margin: ${theme.sz.s2} 0 auto 0;
  `,
);

/**
 * Generate a random rotation amount for the card
 * Uses a seed to ensure this is consistent on each render
 */
export const randomRotation = (() => {
  const cache = new Map<number, number>();
  return (seed: number): number => {
    const existingValue = cache.get(seed);
    if (typeof existingValue !== 'undefined') {
      return existingValue;
    }

    const positiveRand = randomNumber({ min: 0.5, max: 1 });
    const positiveOrNegativeRand = randomSign() * positiveRand;
    cache.set(seed, positiveOrNegativeRand);
    return positiveOrNegativeRand;
  };
})();

const makeContainer = (containerType: 'button' | 'div') =>
  styled(containerType)<{
    $skeletal: boolean;
  }>(({ theme, $skeletal }) => {
    return css`
      ${$skeletal && skeleton}

      align-items: initial;
      align-self: stretch;
      background-color: ${theme.co.neutral99};
      border-radius: ${theme.bo.medium};
      border: ${theme.sz.s05} solid ${theme.co.secondary80};
      box-shadow: 0 ${theme.sz.s1} 0 ${theme.co.secondary80};
      cursor: pointer;
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
      padding: 0;
      position: relative;
      text-align: start;
      transition:
        transform 0.3s ease,
        box-shadow 0.3s ease;
      width: 100%;

      @media (min-width: ${theme.sz.sm}) {
        min-height: ${theme.sz.s64};
        width: ${theme.sz.s48};

        &:hover {
          box-shadow: 0 ${theme.sz.s2} 0 ${theme.co.secondary80};
        }
      }
    `;
  });

export const DivContainer = makeContainer('div');
export const ButtonContainer = makeContainer('button');

export const Container = styled.div(({ theme }) => {
  return css`
    border-radius: ${theme.bo.medium};

    @media (min-width: 30rem) {
      width: ${theme.sz.s48};
    }
  `;
});
